// Package imports:
import React, { useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
// Style imports:
// import './ConfigurationModal.scss';
// Component imports:
import ConfigurationModalAlertTable from './ConfigurationModalAlertTable';
import ConfigurationModalHistoryTable from './ConfigurationModalHistoryTable';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
// Service imports:
import { findMostRecentHistoryEntry, isParameterString } from '../../services/alertUtils';
// Action imports:
import { modifyAlertConfigs } from '../../redux-store/actions/alertActions';
// Type imports:
import { IAlertConfig, IAlertConfigParameterChange, IAlertHardLimitError } from '../../types/api/AlertTypes';
import { RootState } from '../../redux-store/reducers';

interface IOwnProps {
    closeModal(): void,
    alertConfiguration: IAlertConfig
};
type ReduxProps = ConnectedProps<typeof connector>
type Props = ReduxProps & IOwnProps;

type Tabs = 'Alert' | 'History';
const TABS: Tabs[] = ['Alert', 'History'];


/**
 * This is a modal that allows the user to configure an alert.
 * **This is the PopUp!**
 * - Subcomponent of AlertConfig.tsx
 */
const ConfigurationModal: React.FC<Props> = ({
    alertConfiguration,
    closeModal,
    alertConfigs,
    accessTokenV2,
    modifyAlertConfigs
}) => {
    const getInitialParameterConfigurationState = () => {
        const initalParameterConfigurations: IAlertConfigParameterChange[] = alertConfiguration.alertConfigParameters.map((parameter) => {
            const mostRecentHistoryEntry = findMostRecentHistoryEntry(parameter);
            if (mostRecentHistoryEntry) {
                return {
                    alertId: mostRecentHistoryEntry.alert_id,
                    paramId: mostRecentHistoryEntry.param_id,
                    newValue: (isParameterString(mostRecentHistoryEntry.param_id))
                        ? mostRecentHistoryEntry.value_text
                        : mostRecentHistoryEntry.value
                };
            } else {
                return {
                    alertId: parameter.alert_id,
                    paramId: parameter.id,
                    newValue: (isParameterString(parameter.id))
                        ? parameter.default_value_text
                        : parameter.default_value
                };
            }
        });
        return initalParameterConfigurations;
    }

    const [ selectedTab, setSelectedTab ] = useState<Tabs>('Alert');
    const [ parameterConfigurationChanges, setParameterConfigurationChanges ] = useState<IAlertConfigParameterChange[]>(getInitialParameterConfigurationState());
    const [ hardLimitErrors, setHardLimitErrors ] = useState<IAlertHardLimitError[]>([]);

    const checkHardLimitError = (alertId: number, paramId: string, newValue: number | string | null) => {
        // Alert Config error check.
        if (alertConfigs === undefined || alertConfigs === 'waiting' || alertConfigs instanceof Error) return;
        // Check value is number and can be compared.
        if (newValue === null || typeof newValue === 'string') return;
        // Find AlertConfig info.
        const alertConfigParameters = Array.isArray(alertConfigs) 
            ? alertConfigs.find(config => config.id === alertId)?.alertConfigParameters 
            : undefined;
        if (alertConfigParameters === undefined) return;
        // Find Parameter in question
        const alertConfigParameter = alertConfigParameters.find(param => param.id === paramId);
        if (alertConfigParameter === undefined) return;
        // Find default value.
        const defaultValue = alertConfigParameter.default_value;
        if (defaultValue === null) return;

        const HARD_LIMIT_TYPE: {
            [T in string]: undefined | 'default_is_max' | 'default_is_min' | 'min'
        } = {
            'order_age': 'default_is_max',
            'timediff': 'default_is_max',
            'significant_percentage_change': 'default_is_min',
            'order_change_volume': 'default_is_min',
            'large_order_value_shares': 'default_is_min',
            'large_order_value_bonds': 'default_is_min',
            'large_order_value_shares_isk': 'default_is_min',
            'large_order_value_shares_sek': 'default_is_min',
            'large_order_value_shares_dkk': 'default_is_min',
            'large_trade_value_shares_isk': 'default_is_min',
            'large_trade_value_bonds_isk': 'default_is_min',
            'large_trade_value_shares_sek': 'default_is_min',
            'large_trade_value_shares_dkk': 'default_is_min',
            'daydiff': 'default_is_max'
        };

        const newHardLimitErrors = [...hardLimitErrors];
        const indexOfCurrentCheck = newHardLimitErrors.findIndex(hardLimitError => hardLimitError.configParamId === paramId);
        if (indexOfCurrentCheck !== -1) newHardLimitErrors.splice(indexOfCurrentCheck, 1);

        const hardLimitType = HARD_LIMIT_TYPE[paramId];
        if (hardLimitType === undefined) return;
        // if (newValue) (type (<= or >=)) (Hard_limit) then good.
        if (hardLimitType === 'default_is_max' && newValue > defaultValue) {
            const newHardLimitError: IAlertHardLimitError = {
                configParamId: alertConfigParameter.id,
                errorMessage: `The maximum value for "${alertConfigParameter.description}" is ${defaultValue}`
            }
            newHardLimitErrors.push(newHardLimitError);
        }
        if (hardLimitType === 'default_is_max' && newValue <= 0) {
            const newHardLimitError: IAlertHardLimitError = {
                configParamId: alertConfigParameter.id,
                errorMessage: `The value for "${alertConfigParameter.description}" must be positive`
            }
            newHardLimitErrors.push(newHardLimitError);
        }
        if (hardLimitType === 'default_is_min' && newValue < defaultValue) {
            const newHardLimitError: IAlertHardLimitError = {
                configParamId: alertConfigParameter.id,
                errorMessage: `The minimum value for "${alertConfigParameter.description}" is ${defaultValue}`
            }
            newHardLimitErrors.push(newHardLimitError);
        }
        setHardLimitErrors(newHardLimitErrors);
    }

    const handleConfigParameterChange = (alertId: number, paramId: string, newValue: number | string | null) => {
        const newParameterChange: IAlertConfigParameterChange = {
            alertId,
            paramId,
            newValue
        };
        const existingIndex = parameterConfigurationChanges.findIndex(configChange => (
            configChange.alertId === alertId
            && configChange.paramId === paramId
        ));

        checkHardLimitError(alertId, paramId, newValue);

        if (existingIndex === -1) {
            setParameterConfigurationChanges(parameterConfigurationChanges.concat([newParameterChange]));
        } else {
            const parameterConfigurationChangesCopy = [...parameterConfigurationChanges];
            parameterConfigurationChangesCopy[existingIndex] = newParameterChange;
            setParameterConfigurationChanges(parameterConfigurationChangesCopy);
        }
    }

    const handleAlertConfigChangeSave = () => {
        if (alertConfigs === undefined || alertConfigs === 'waiting' || alertConfigs instanceof Error) return;
        // Check if new value isn't a change.
        const parameterConfigurationChangesWithoutRedundancies: IAlertConfigParameterChange[] = [];
        for (let configurationChange of parameterConfigurationChanges) {
            const { alertId, paramId, newValue } = configurationChange;
            // Find alert.
            const alertConfig = Array.isArray(alertConfigs) ? alertConfigs.find(config => config.id === alertId) : undefined;
            // Find parameter.
            const configParameter = alertConfig?.alertConfigParameters?.find(param => param.id === paramId);
            if (configParameter !== undefined) {
                // Find most recent value for parameter.
                const mostRecentParameterConfig = findMostRecentHistoryEntry(configParameter);
                if (mostRecentParameterConfig === undefined) {
                    parameterConfigurationChangesWithoutRedundancies.push(configurationChange);
                } else {
                    // If new value is different from most recent value, then it should be changed.
                    if (newValue !== mostRecentParameterConfig.value) {
                        parameterConfigurationChangesWithoutRedundancies.push(configurationChange);
                    }
                }
            } else {
                parameterConfigurationChangesWithoutRedundancies.push(configurationChange);
            }
        }
        // After finding all changes without redundancies, then request to change them.
        if (parameterConfigurationChangesWithoutRedundancies.length > 0) {
            modifyAlertConfigs(accessTokenV2, parameterConfigurationChangesWithoutRedundancies);
        }
        closeModal();
    }

    return (
        <Modal
            show
            size='lg'
            backdrop='static'
            onHide={() => closeModal()}
            centered
            id='ConfigurationModal'
        >
            <Modal.Header closeButton>
                <Modal.Title>
                    {alertConfiguration.description}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className='PanelTabSelector'>
                    <div className='BackgroundBar' />
                    <div className='PanelTabs'>
                        {
                            TABS
                            // Turn array into JSX.
                            .map(tabTitle => (
                                <div
                                    key={tabTitle}
                                    onClick={() => setSelectedTab(tabTitle)}
                                    className={'PanelTab' + ((selectedTab === tabTitle) ? ' SelectedTab' : '')}
                                >
                                    {
                                        (selectedTab === tabTitle)
                                        ? <div className='SelectedTabBackground' />
                                        : null
                                    }
                                    {tabTitle}
                                </div>
                            ))
                        }
                    </div>
                </div>
                {
                    (selectedTab === 'Alert')
                    ? (
                        <ConfigurationModalAlertTable
                            alertConfiguration={alertConfiguration}
                            parameterConfigurationChanges={parameterConfigurationChanges}
                            handleConfigParameterChange={handleConfigParameterChange}
                            errorMessages={hardLimitErrors.map(x => x.errorMessage)}
                        />
                    ) : (
                        <ConfigurationModalHistoryTable
                            alertConfiguration={alertConfiguration}
                        />
                    )
                }
            </Modal.Body>
            <Modal.Footer>
                <Button
                    variant='dark'
                    className='CloseButton'
                    onClick={() => closeModal()}
                >
                    Cancel
                </Button>
                <Button
                    variant='dark'
                    className='SaveButton'
                    disabled={selectedTab === 'History' || parameterConfigurationChanges.length === 0 || hardLimitErrors.length !== 0}
                    onClick={() => handleAlertConfigChangeSave()}
                >
                    Save
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

function mapStateToProps({ alertReducer, loginReducer }: RootState) {
    const { alertConfigs } = alertReducer;
    const { accessTokenV2 } = loginReducer;
    return {
        alertConfigs,
        accessTokenV2
    };
}

const connector = connect(mapStateToProps, { modifyAlertConfigs });
export default connector(ConfigurationModal);